import { isEqual } from 'lodash';

export type JourneyVariantInput = {
  name?: string;
  criteria?: Record<string, unknown> | null;
  template?: { ID?: string; type?: string } | null;
};
export type JourneyGroupInput = {
  groupID: string;
  channel?: string;
  variants?: JourneyVariantInput[] | null;
};
export type JourneyTemplateMatch =
  | { status: 'matched'; groupID: string; variantName: string; templateID: string; channel: string }
  | { status: 'no-match' | 'ambiguous' | 'invalid'; groupID: string; variantName?: string; message: string };

type Operator = 'eq' | '!eq' | 'gt' | 'ge' | 'lt' | 'le' | 'in' | '!in';
const OPERATORS = new Set<string>(['eq', '!eq', 'gt', 'ge', 'lt', 'le', 'in', '!in']);
const ORDERING = new Set<string>(['gt', 'ge', 'lt', 'le']);
const hasOwn = (object: object, key: string) => Object.prototype.hasOwnProperty.call(object, key);
export const isJourneyAttributesObject = (value: unknown): value is Record<string, unknown> => (
  value !== null && typeof value === 'object' && !Array.isArray(value)
  && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)
);
const isJSONValue = (value: unknown): boolean => {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return true;
  if (typeof value === 'number') return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(isJSONValue);
  return isJourneyAttributesObject(value) && Object.values(value).every(isJSONValue);
};

// Attribute objects are operator maps; plain scalar/array values are implicit eq.
const conditionsFor = (criterion: unknown): [Operator, unknown][] | null => {
  if (!isJourneyAttributesObject(criterion)) {
    return isJSONValue(criterion) ? [['eq', criterion]] : null;
  }
  const entries = Object.entries(criterion);
  if (!entries.length || entries.some(([operator, operand]) => (
    !OPERATORS.has(operator) || !isJSONValue(operand)
    || (ORDERING.has(operator) && (typeof operand !== 'number' || !Number.isFinite(operand)))
    || ((operator === 'in' || operator === '!in') && !Array.isArray(operand))
  ))) return null;
  return entries as [Operator, unknown][];
};

const evaluateCondition = (actual: unknown, operator: Operator, operand: unknown): boolean => {
  switch (operator) {
    case 'eq': return isEqual(actual, operand);
    case '!eq': return !isEqual(actual, operand);
    case 'in':
    case '!in': {
      if (actual !== null && typeof actual === 'object') return false;
      const included = (operand as unknown[]).some(value => isEqual(actual, value));
      return operator === 'in' ? included : !included;
    }
    default: {
      if (typeof actual !== 'number' || !Number.isFinite(actual)) return false;
      const expected = operand as number;
      switch (operator) {
        case 'gt': return actual > expected;
        case 'ge': return actual >= expected;
        case 'lt': return actual < expected;
        case 'le': return actual <= expected;
        default: return false;
      }
    }
  }
};

const normalizedChannel = (value?: string) => {
  if (typeof value !== 'string') return '';
  const channel = value.trim().toUpperCase();
  return channel === 'PUSHINBOX' ? 'PCI' : channel;
};

/** Evaluate every criterion within a group before exposing any mapped templates. */
const findGroupTemplates = (
  groupID: string,
  groups: JourneyGroupInput[],
  attributes: Record<string, unknown>,
): JourneyTemplateMatch[] => {
  const invalid = (message: string): JourneyTemplateMatch[] => [{ status: 'invalid', groupID, message }];
  if (!isJourneyAttributesObject(attributes) || !isJSONValue(attributes)) {
    return invalid('User attributes must be a JSON object.');
  }
  const selected = Array.isArray(groups) ? groups.filter(group => group && group.groupID === groupID) : [];
  if (!groupID || selected.length !== 1) return invalid('Select a valid, unique Group ID.');
  const group = selected[0];
  if (!Array.isArray(group.variants) || !group.variants.length) {
    return invalid('No synchronized template variants are available for this Group ID.');
  }

  const matches: JourneyVariantInput[] = [];
  for (const variant of group.variants) {
    if (!variant || !isJourneyAttributesObject(variant.criteria)) {
      return invalid('A variant has invalid or missing criteria.');
    }
    let matchesVariant = true;
    for (const [attribute, criterion] of Object.entries(variant.criteria)) {
      const conditions = conditionsFor(criterion);
      if (!conditions) return invalid('A variant has invalid criteria or an unsupported operator.');
      if (!hasOwn(attributes, attribute)
        || !conditions.every(([operator, operand]) => evaluateCondition(attributes[attribute], operator, operand))) {
        matchesVariant = false;
      }
    }
    if (matchesVariant) matches.push(variant);
  }
  if (!matches.length) return [{ status: 'no-match', groupID, message: 'No matching template criteria found.' }];
  return matches.map((match): JourneyTemplateMatch => {
    const templateID = match.template && match.template.ID;
    const channel = normalizedChannel(match.template && match.template.type || group.channel);
    const groupChannel = normalizedChannel(group.channel);
    if (typeof templateID !== 'string' || !templateID.trim()
      || !['PUSH', 'EMAIL', 'PCI'].includes(channel)
      || (groupChannel && groupChannel !== channel)) {
      return { status: 'invalid', groupID, variantName: match.name || '', message: 'The matched variant has an invalid template mapping.' };
    }
    return { status: 'matched', groupID, variantName: match.name || '', templateID: templateID.trim(), channel };
  });
};

/** Backwards-compatible single-group resolution: multiple matches are ambiguous. */
export const findJourneyTemplate = (
  groupID: string,
  groups: JourneyGroupInput[],
  attributes: Record<string, unknown>,
): JourneyTemplateMatch => {
  const matches = findGroupTemplates(groupID, groups, attributes);
  if (matches.length > 1) {
    return { status: 'ambiguous', groupID, message: 'Multiple template variants match. A unique match is required.' };
  }
  const match = matches[0];
  // Keep the original single-result error shape for existing callers.
  if (match.status !== 'matched') {
    return { status: match.status, groupID: match.groupID, message: match.message };
  }
  return match;
};

/** Find every matching variant across all groups; selection belongs to the UI. */
export const findJourneyTemplates = (
  groups: JourneyGroupInput[],
  attributes: Record<string, unknown>,
): JourneyTemplateMatch[] => {
  if (!isJourneyAttributesObject(attributes) || !isJSONValue(attributes)) {
    return [{ status: 'invalid', groupID: '', message: 'User attributes must be a JSON object.' }];
  }
  if (!Array.isArray(groups) || !groups.length) {
    return [{ status: 'invalid', groupID: '', message: 'No synchronized template variants are available.' }];
  }
  const groupIDs = Array.from(new Set(groups.map(group => group && group.groupID || '')));
  return groupIDs.flatMap((groupID): JourneyTemplateMatch[] => {
    if (!groupID || groups.filter(group => group && group.groupID === groupID).length !== 1) {
      return [{ status: 'invalid', groupID, message: 'Campaign contains duplicate or invalid Group IDs.' }];
    }
    return findGroupTemplates(groupID, groups, attributes);
  });
};
